import { Api } from "metabase/api";
import { idTag, listTag } from "metabase/api/tags";
import {
  fetchDashboard,
  fetchDashboardCardData,
  setParameterValueToDefault,
} from "metabase/dashboard/actions";
import {
  getDashboardId,
  getIsEditing,
  getParameters,
} from "metabase/dashboard/selectors";
import { isEmbeddingSdk } from "metabase/embedding-sdk/config";
import type { MetabotDashboardUpdatedHandler } from "metabase/metabot";
import type { Dispatch, GetState } from "metabase/redux/store";
import { navigate } from "metabase/router";
import * as Urls from "metabase/urls";
import type { DashboardId } from "metabase-types/api";

/**
 * Reloads the dashboard `dashboardId` and its cards' data when it is the one open, keeping the filter values the
 * user picked and starting filters added since on their defaults. A dashboard open in its editor is left alone, so
 * unsaved edits are never replaced. Resolves to whether it reloaded.
 */
export const reloadOpenDashboard =
  (dashboardId: DashboardId) =>
  async (dispatch: Dispatch, getState: GetState): Promise<boolean> => {
    const state = getState();
    const isOpen = getDashboardId(state) === dashboardId;
    if (!isOpen || getIsEditing(state)) {
      return false;
    }
    const parameterIdsBefore = new Set(
      getParameters(state).map(({ id }) => id),
    );
    await dispatch(
      fetchDashboard({
        dashId: dashboardId,
        queryParams: {},
        options: { preserveParameters: true },
      }),
    );
    getParameters(getState())
      .filter(
        (parameter) =>
          !parameterIdsBefore.has(parameter.id) && parameter.default != null,
      )
      .forEach(({ id }) => dispatch(setParameterValueToDefault(id)));
    await dispatch(fetchDashboardCardData({ reload: false, clearCache: true }));
    return true;
  };

/**
 * What happens when Metabot saves a dashboard: a dashboard it created is opened, unless the chat is the whole page;
 * a dashboard it changed is reloaded when it is open.
 */
export const handleDashboardUpdated: MetabotDashboardUpdatedHandler = ({
  value,
  isFullPageMetabot,
  dispatch,
}) => {
  dispatch(
    Api.util.invalidateTags([
      idTag("dashboard", value.dashboard_id),
      listTag("dashboard"),
    ]),
  );

  if (value.created) {
    if (!isFullPageMetabot && !isEmbeddingSdk()) {
      navigate(Urls.dashboard({ id: value.dashboard_id, name: value.name }));
    }
    return;
  }

  void dispatch(reloadOpenDashboard(value.dashboard_id));
};
